import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../db';

function ProgressBar({ value, height = 6 }) {
  return (
    <div style={{ height, borderRadius: height, background: '#e5e7eb', overflow: 'hidden' }}>
      <div style={{
        height: '100%',
        width: value + '%',
        background: value >= 100 ? '#FF6F0F' : value >= 60 ? '#FF6F0F' : value >= 30 ? '#A1A1AA' : '#e5e7eb',
        borderRadius: height,
        transition: 'width 0.4s ease',
      }} />
    </div>
  );
}

const fmt = (n) => n ? n.toLocaleString() + '원' : '-';

export default function ShareView({ shareId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!shareId) { setError('잘못된 링크입니다.'); setLoading(false); return; }
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'shares', shareId));
        if (!snap.exists()) { setError('링크가 만료되었거나 존재하지 않습니다.'); }
        else { setData(snap.data()); }
      } catch (e) {
        setError('견적서를 불러올 수 없습니다. 링크가 유효한지 확인해주세요.');
      }
      setLoading(false);
    };
    load();
  }, [shareId]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px', color: '#18181B' }}>
        <div style={{ fontSize: '40px' }}>🏗️</div>
        <p style={{ fontWeight: '700' }}>견적서 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px', color: '#52525B', padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '40px' }}>⚠️</div>
        <p style={{ fontWeight: '700', fontSize: '16px' }}>{error}</p>
      </div>
    );
  }

  const overall = data.stages && data.stages.length > 0
    ? Math.round(data.stages.reduce((s, st) => s + (st.progress || 0), 0) / data.stages.length)
    : null;

  const STATUS_LABEL = { estimate: '견적', construction: '공사중', complete: '완료' };
  const STATUS_COLOR = { estimate: '#EA580C', construction: '#2563eb', complete: '#FF6F0F' };
  const STATUS_BG    = { estimate: '#F4F4F5', construction: '#eff6ff', complete: '#F4F4F5' };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      {/* 헤더 */}
      <div style={{ background: '#FF6F0F', color: '#fff', padding: '16px 20px' }}>
        <p style={{ margin: '0 0 2px', fontSize: '13px', opacity: 0.75 }}>셈핏 인테리어 견적서</p>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800' }}>{data.customerName} 고객님</h1>
        {data.address && <p style={{ margin: '6px 0 0', fontSize: '13px', opacity: 0.85 }}>📍 {data.address}</p>}
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
          {data.area > 0 && <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' }}>📐 {data.area}㎡</span>}
          {data.status && (
            <span style={{ background: STATUS_BG[data.status] || '#F4F4F5', color: STATUS_COLOR[data.status] || '#EA580C', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' }}>
              {STATUS_LABEL[data.status] || data.status}
            </span>
          )}
          {data.sharedAt && (
            <span style={{ background: 'rgba(255,255,255,0.15)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', opacity: 0.8 }}>
              {new Date(data.sharedAt).toLocaleDateString('ko-KR')} 발송
            </span>
          )}
        </div>
      </div>

      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>

        {/* 공정 진행률 */}
        {overall !== null && (
          <div style={{ background: '#fff', borderRadius: '14px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '800' }}>📊 공정 진행률</h2>
              <span style={{ fontSize: '20px', fontWeight: '900', color: overall >= 100 ? '#FF6F0F' : '#FF6F0F' }}>
                {overall}%
              </span>
            </div>
            <ProgressBar value={overall} height={10} />
            {data.stages && data.stages.length > 0 && (
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {data.stages.map((stage, idx) => (
                  <div key={stage.id || idx}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{
                        fontSize: '13px', fontWeight: '600',
                        color: stage.progress >= 100 ? '#FF6F0F' : '#374151',
                        textDecoration: stage.progress >= 100 ? 'line-through' : 'none',
                        textDecorationColor: '#FF6F0F',
                        display: 'flex', alignItems: 'center', gap: '6px',
                      }}>
                        {stage.progress >= 100
                          ? <span style={{ color: '#18181B', fontSize: '14px' }}>✓</span>
                          : <span style={{ color: '#9ca3af', fontSize: '12px' }}>{idx + 1}</span>
                        }
                        {stage.name}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: stage.progress >= 100 ? '#FF6F0F' : '#374151' }}>
                        {stage.progress}%
                      </span>
                    </div>
                    <ProgressBar value={stage.progress} height={5} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 견적 3안 */}
        {data.estimate && (
          <div style={{ background: '#fff', borderRadius: '14px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '800' }}>📋 견적 안내</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              {[
                { key: 'low',  label: '합리적', emoji: '💛', color: '#EA580C', bg: '#FAFAFA' },
                { key: 'mid',  label: '표준', emoji: '💚', color: '#18181B', bg: '#FAFAFA' },
                { key: 'high', label: '프리미엄', emoji: '💎', color: '#7c3aed', bg: '#f5f3ff' },
              ].map(({ key, label, emoji, color, bg }) => (
                <div key={key} style={{
                  background: bg, borderRadius: '12px', padding: '14px 10px', textAlign: 'center',
                  border: '1px solid ' + color + '33',
                  boxShadow: key === 'mid' ? '0 0 0 2px ' + color + '66' : 'none',
                }}>
                  <div style={{ fontSize: '20px', marginBottom: '4px' }}>{emoji}</div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color, marginBottom: '8px' }}>{label}</div>
                  <div style={{ fontSize: '16px', fontWeight: '900', color: '#111827' }}>
                    {(data.estimate[key] / 10000).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '10px', color: '#9ca3af' }}>만원</div>
                </div>
              ))}
            </div>
            <p style={{ margin: '12px 0 0', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
              * 현장 상황에 따라 견적이 달라질 수 있습니다
            </p>
          </div>
        )}

        {/* 메모 */}
        {data.note && (
          <div style={{ background: '#fff', borderRadius: '14px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <h2 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: '800' }}>📝 메모</h2>
            <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{data.note}</p>
          </div>
        )}

        {/* 푸터 */}
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: '12px' }}>
          <p style={{ margin: 0 }}>🏗️ 셈핏으로 작성된 견적서입니다</p>
        </div>
      </div>
    </div>
  );
}
